import { ReactNode } from 'react'
import { Navigate, NavLink, Outlet, Route, Routes } from 'react-router-dom'
import Select, { SingleValue } from 'react-select'
import { PageLink, PageTitle } from '../../../_metronic/layout/core'
import DocumentTitle from 'react-document-title'
import AiForecastView from './AiForecastView'
import AiAccuracyView from './AiAccuracyView'
import AiAutoEventsView from './AiAutoEventsView'
import AiModelStatusView from './AiModelStatusView'
import AiDataAnalysisView from './AiDataAnalysisView'
import {
  AiFilterProvider,
  CompanyOption,
  HorizonOption,
  selectStyles,
  useAiFilters,
} from './AiFilterContext'
import { SiteOption } from './AiFilterContext'

const breadcrumbs: Array<PageLink> = [
  { title: 'AI Analytics', path: '/ai-preview/forecast', isSeparator: false, isActive: false },
  { title: '', path: '', isSeparator: true, isActive: false },
]

const tabs = [
  { to: '/ai-preview/forecast', label: 'Forecast' },
  { to: '/ai-preview/data-analysis', label: 'Data Analysis' },
  { to: '/ai-preview/accuracy', label: 'Accuracy' },
  { to: '/ai-preview/auto-events', label: 'Events' },
  { to: '/ai-preview/model', label: 'Model' },
]

const FilterBar = () => {
  const { company, setCompany, site, setSite, horizon, setHorizon, companies, sites, horizons } =
    useAiFilters()

  return (
    <div className='card mb-5 border-0 shadow-sm'>
      <div className='card-body py-4 px-5'>
        <div className='row g-4 align-items-end'>
          <div className='col-12 col-md-4'>
            <label className='form-label fs-7 text-gray-600 mb-1'>Company</label>
            <Select<CompanyOption, false>
              classNamePrefix='react-select'
              styles={selectStyles}
              value={company}
              onChange={(opt: SingleValue<CompanyOption>) => opt && setCompany(opt)}
              options={companies}
              isSearchable={false}
            />
          </div>
          <div className='col-12 col-md-4'>
            <label className='form-label fs-7 text-gray-600 mb-1'>Site</label>
            <Select<SiteOption, false>
              classNamePrefix='react-select'
              styles={selectStyles}
              value={site}
              onChange={(opt: SingleValue<SiteOption>) => opt && setSite(opt)}
              options={sites}
              getOptionLabel={(o: SiteOption) => o.label}
              formatOptionLabel={(o: SiteOption) => (
                <div className='d-flex align-items-center justify-content-between gap-3'>
                  <span>{o.label}</span>
                  <span className='badge badge-light fs-9 text-capitalize'>
                    {o.type.replace(/_/g, ' ')}
                  </span>
                </div>
              )}
            />
          </div>
          <div className='col-12 col-md-4'>
            <label className='form-label fs-7 text-gray-600 mb-1'>Horizon</label>
            <Select<HorizonOption, false>
              classNamePrefix='react-select'
              styles={selectStyles}
              value={horizon}
              onChange={(opt: SingleValue<HorizonOption>) => opt && setHorizon(opt)}
              options={horizons}
              isSearchable={false}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

const Shell = ({ title, children }: { title: string; children: ReactNode }) => (
  <>
    <DocumentTitle title={`${process.env.REACT_APP_NAME} | ${title}`} />
    <PageTitle breadcrumbs={breadcrumbs}>{title}</PageTitle>

    <FilterBar />

    <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-6'>
      <ul className='nav nav-pills nav-pills-custom gap-1 mb-0'>
        {tabs.map((t) => (
          <li className='nav-item' key={t.to}>
            <NavLink
              to={t.to}
              className={({ isActive }) =>
                `nav-link px-4 py-2 rounded-pill fs-7 fw-semibold ${
                  isActive ? 'active bg-primary text-white' : 'bg-light text-gray-700'
                }`
              }
            >
              {t.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>

    {children}
  </>
)

const AiPreviewPage = () => (
  <AiFilterProvider>
    <Routes>
      <Route element={<Outlet />}>
        <Route
          path='forecast'
          element={
            <Shell title='Forecast'>
              <AiForecastView />
            </Shell>
          }
        />
        <Route
          path='data-analysis'
          element={
            <Shell title='Data Analysis'>
              <AiDataAnalysisView />
            </Shell>
          }
        />
        <Route
          path='accuracy'
          element={
            <Shell title='Accuracy'>
              <AiAccuracyView />
            </Shell>
          }
        />
        <Route
          path='auto-events'
          element={
            <Shell title='Events'>
              <AiAutoEventsView />
            </Shell>
          }
        />
        <Route
          path='model'
          element={
            <Shell title='Model'>
              <AiModelStatusView />
            </Shell>
          }
        />
        <Route index element={<Navigate to='/ai-preview/forecast' replace />} />
      </Route>
    </Routes>
  </AiFilterProvider>
)

export default AiPreviewPage
